import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { CampaignStatus } from '@/shared/types/enums';
import styles from './CampaignStatusBadge.module.scss';

export interface CampaignStatusBadgeProps {
  status: CampaignStatus;
  className?: string;
  /**
   * Platform-specific `effective_status` (e.g. Meta terminology). Surfaced in
   * the badge's native tooltip when it diverges from {@link status} so the
   * user can see the platform's view alongside Promote's.
   */
  effectiveStatus?: string | null;
  /** ISO timestamp of the most recent ad-platform status sync. */
  lastSyncedAt?: string | null;
}

function buildTooltip(
  status: CampaignStatus,
  effectiveStatus: string | null | undefined,
  lastSyncedAt: string | null | undefined,
): string | undefined {
  const parts: string[] = [];
  if (effectiveStatus && effectiveStatus !== status) {
    parts.push(`Platform: ${effectiveStatus.replace(/_/g, ' ').toLowerCase()}`);
  }
  if (lastSyncedAt) {
    parts.push(`Synced ${new Date(lastSyncedAt).toLocaleString()}`);
  }
  return parts.length ? parts.join(' · ') : undefined;
}

const STATUS_VARIANT_MAP: Record<CampaignStatus, string> = {
  [CampaignStatus.DRAFT]: 'draft',
  [CampaignStatus.PENDING_REVIEW]: 'pending',
  [CampaignStatus.APPROVED]: 'approved',
  [CampaignStatus.REJECTED]: 'rejected',
  [CampaignStatus.ACTIVE]: 'active',
  [CampaignStatus.PAUSED]: 'paused',
  [CampaignStatus.COMPLETED]: 'completed',
  [CampaignStatus.FAILED]: 'failed',
  [CampaignStatus.ARCHIVED]: 'archived',
};

export function CampaignStatusBadge({
  status,
  className,
  effectiveStatus,
  lastSyncedAt,
}: CampaignStatusBadgeProps) {
  const { t } = useTranslation();
  const variant = STATUS_VARIANT_MAP[status] ?? 'draft';
  const statusKey = status.toLowerCase().replace('_', '_') as string;
  const tooltip = buildTooltip(status, effectiveStatus, lastSyncedAt);

  return (
    <span className={clsx(styles.badge, styles[variant], className)} title={tooltip}>
      <span className={styles.dot} />
      {t(`status.${statusKey}`)}
    </span>
  );
}
